import { BookA, GraduationCap, MonitorPlay, PowerOff, Route } from 'lucide-react';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Portal } from '../../components/ui/layer';
import { Menu, type MenuItem, useMenu } from '../../components/ui/Menu';
import { useToast } from '../../components/ui/Toast';
import { cn } from '../../lib/cn';
import { useLocalStorage } from '../../lib/hooks';
import { type ActiveTour, type TourEndReason, TourLayer } from './TourLayer';
import { getTour, sanitizeSteps, screenKey, type TourId, type TourStep, tourForLocation } from './tours';

/** Ключ режиму навчання в localStorage (зручність окремого браузера; синхронізується між вкладками). */
export const TRAINING_STORAGE_KEY = 'ers.training';
/** Тури, які вже запускали в цьому браузері (для позначки «є новий тур» на кнопці). */
const SEEN_STORAGE_KEY = 'ers.training.seen';
/** Довідник понять бази знань. */
export const TERMS_PATH = '/knowledge/terms';

export type StartTourOptions = {
  /** Демонстрація: кроки перемикаються самі (~4,5 с), пауза при наведенні чи фокусі. */
  demo?: boolean;
  /** Власні кроки (напр., з довідки процесу) замість вбудованих; tourId тоді — довільний ідентифікатор. */
  steps?: readonly Partial<TourStep>[];
  /** Назва тура в картці. */
  title?: string;
  /** Примітка, коли цілі кроку немає на екрані. */
  missingHint?: string;
};

export type TrainingContextValue = {
  /** Режим навчання ввімкнено: видно кнопку «Навчання» й підказки «?». */
  enabled: boolean;
  setEnabled: (value: boolean) => void;
  toggle: () => void;
  /** Запускає тур (працює й без режиму навчання). false — тура немає або в ньому немає кроків. */
  startTour: (tourId: string, options?: StartTourOptions) => boolean;
  stopTour: () => void;
  activeTour: ActiveTour | null;
  /** Вбудований тур для поточного екрана (за адресою) або null. */
  screenTourId: TourId | null;
};

const TrainingContext = createContext<TrainingContextValue | null>(null);

let warned = false;
function warnMissingProvider() {
  if (import.meta.env.DEV && !warned) {
    warned = true;
    console.warn('[навчання] TrainingProvider не підключено: режим навчання й тури недоступні.');
  }
}

/** Без провайдера — безпечна заглушка (режим вимкнено), щоб екрани не падали під час поетапної інтеграції. */
const FALLBACK: TrainingContextValue = {
  enabled: false,
  setEnabled: warnMissingProvider,
  toggle: warnMissingProvider,
  startTour: () => {
    warnMissingProvider();
    return false;
  },
  stopTour: () => {},
  activeTour: null,
  screenTourId: null,
};

export function useTraining(): TrainingContextValue {
  return useContext(TrainingContext) ?? FALLBACK;
}

type RunningTour = ActiveTour & { returnFocus: HTMLElement | null; screen: string };

let tourSeq = 0;

/**
 * Режим навчання: стан (localStorage `ers.training`), запуск турів, шар тура й плаваюча кнопка «Навчання».
 * Має стояти всередині роутера (використовує адресу) і всередині ToastProvider.
 */
export function TrainingProvider({ children }: { children: ReactNode }) {
  const [stored, setStored] = useLocalStorage<boolean>(TRAINING_STORAGE_KEY, false);
  const [seen, setSeen] = useLocalStorage<string[]>(SEEN_STORAGE_KEY, []);
  const enabled = stored === true;
  const [active, setActive] = useState<RunningTour | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const screenTourId = tourForLocation(location.pathname, location.search);

  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  const startTour = useCallback(
    (tourId: string, options: StartTourOptions = {}) => {
      const def = getTour(tourId);
      const steps = sanitizeSteps(options.steps?.length ? options.steps : (def?.steps ?? []));
      if (!steps.length) {
        if (import.meta.env.DEV) console.warn(`[навчання] Тур «${tourId}» не знайдено або в ньому немає кроків.`);
        return false;
      }
      const focused = document.activeElement;
      setActive({
        key: ++tourSeq,
        id: tourId,
        title: options.title ?? def?.title ?? 'Навчальний тур',
        steps,
        demo: !!options.demo,
        missingHint: options.missingHint ?? def?.missingHint,
        returnFocus: focused instanceof HTMLElement && focused !== document.body ? focused : null,
        screen: screenKey(pathRef.current),
      });
      setSeen((prev) => (Array.isArray(prev) && prev.includes(tourId) ? prev : [...(Array.isArray(prev) ? prev : []), tourId].slice(-50)));
      return true;
    },
    [setSeen],
  );

  const stopTour = useCallback(() => setActive(null), []);
  const endTour = useCallback((_reason: TourEndReason) => setActive(null), []);

  const setEnabled = useCallback(
    (value: boolean) => {
      if (value === enabledRef.current) return;
      setStored(value);
      if (value) {
        toast.info('Режим навчання увімкнено', 'Кнопка «Навчання» праворуч унизу запускає тур по екрану й демонстрацію, а біля правил фірми з’являються підказки «?».');
      } else {
        setActive(null);
        toast.info('Режим навчання вимкнено', 'Увімкнути його знову можна в меню профілю.', { label: 'Увімкнути знову', onClick: () => setStored(true) });
      }
    },
    [setStored, toast],
  );
  const toggle = useCallback(() => setEnabled(!enabledRef.current), [setEnabled]);

  // Перехід на інший екран завершує тур (перемикання подань проєкту чи розділів адміністрування — ні).
  useEffect(() => {
    setActive((a) => (a && a.screen !== screenKey(location.pathname) ? null : a));
  }, [location.pathname]);

  const value = useMemo<TrainingContextValue>(
    () => ({ enabled, setEnabled, toggle, startTour, stopTour, activeTour: active, screenTourId }),
    [enabled, setEnabled, toggle, startTour, stopTour, active, screenTourId],
  );

  const hasNewTour = !!screenTourId && !(Array.isArray(seen) && seen.includes(screenTourId));

  return (
    <TrainingContext.Provider value={value}>
      {children}
      {enabled && (
        <TrainingButton
          screenTourId={screenTourId}
          hasNewTour={hasNewTour && !active}
          onTour={(demo) => screenTourId && startTour(screenTourId, { demo })}
          onTerms={() => navigate(TERMS_PATH)}
          onDisable={() => setEnabled(false)}
          locationKey={`${location.pathname}${location.search}`}
        />
      )}
      {active && <TourLayer key={active.key} tour={active} onClose={endTour} returnFocus={active.returnFocus} />}
    </TrainingContext.Provider>
  );
}

/**
 * Плаваюча кнопка «Навчання» (праворуч унизу, над нижньою навігацією телефона) з меню режиму.
 * Коли праворуч відкрито картку задачі (на широкому екрані), кнопка стає ліворуч від неї, щоб не закривати поле коментаря.
 */
function TrainingButton({
  screenTourId,
  hasNewTour,
  onTour,
  onTerms,
  onDisable,
  locationKey,
}: {
  screenTourId: TourId | null;
  hasNewTour: boolean;
  onTour: (demo: boolean) => void;
  onTerms: () => void;
  onDisable: () => void;
  locationKey: string;
}) {
  const menu = useMenu();
  const [right, setRight] = useState<number | null>(null);
  const tour = screenTourId ? getTour(screenTourId) : undefined;

  useLayoutEffect(() => {
    const update = () => {
      const panel = document.querySelector<HTMLElement>('[data-tour="task-card"]');
      if (!panel || window.innerWidth < 1024) {
        setRight(null);
        return;
      }
      const r = panel.getBoundingClientRect();
      setRight(r.left > 0 && r.left < window.innerWidth - 40 ? Math.round(window.innerWidth - r.left + 16) : null);
    };
    update();
    // Панель задачі з’являється з короткою анімацією — уточнюємо положення після неї.
    const t = window.setTimeout(update, 260);
    window.addEventListener('resize', update);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener('resize', update);
    };
  }, [locationKey]);

  const items: MenuItem[] = [
    { type: 'label', key: 'label', label: 'Режим навчання' },
    {
      key: 'tour',
      label: 'Тур по цьому екрану',
      icon: <Route className="size-4" />,
      description: tour ? tour.title : 'Для цього екрана туру ще немає',
      disabled: !tour,
      onSelect: () => onTour(false),
    },
    {
      key: 'demo',
      label: 'Показати демонстрацію',
      icon: <MonitorPlay className="size-4" />,
      description: tour ? 'Той самий тур, кроки перемикаються самі' : undefined,
      disabled: !tour,
      onSelect: () => onTour(true),
    },
    { key: 'terms', label: 'Довідник понять', icon: <BookA className="size-4" />, onSelect: onTerms },
    { type: 'separator', key: 'sep' },
    { key: 'off', label: 'Вимкнути режим навчання', icon: <PowerOff className="size-4" />, onSelect: onDisable },
  ];

  return (
    <Portal>
      <button
        type="button"
        data-training-layer=""
        data-keep-panel=""
        {...menu.triggerProps}
        className={cn(
          'ers-press fixed right-4 bottom-[calc(72px+env(safe-area-inset-bottom))] z-[45] inline-flex h-10 items-center gap-2 rounded-md border border-line-strong bg-raised px-3 text-body font-medium text-fg shadow-pop',
          'hover:bg-surface-2 sm:bottom-4',
        )}
        style={right != null ? { right } : undefined}
      >
        <GraduationCap className="size-4 shrink-0 text-info-fg" aria-hidden />
        <span className="sr-only sm:not-sr-only">Навчання</span>
        {hasNewTour && (
          <>
            <span aria-hidden className="absolute -top-1 -right-1 size-2.5 rounded-full border-2 border-raised bg-info" />
            <span className="sr-only">: для цього екрана є тур</span>
          </>
        )}
      </button>
      <Menu open={menu.open} onClose={menu.close} anchor={menu.anchor} items={items} label="Режим навчання" placement="top-end" className="min-w-[260px]" />
    </Portal>
  );
}
